import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMG } from "./siteData";
import { useAdminImages, ChangeBtn } from "./AboutPremium";

const EASE = [0.22, 1, 0.36, 1];
const STEPS = ["Printed", "Fused", "Cut", "Stitched"];
const MARQUEE = ["Digital printing", "Kurtis & dress materials", "Event drapes", "Wedding backdrops",
  "Stage backdrops", "Fusing & cutting", "Couture stitching"];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap');
@keyframes nv-drift{0%{transform:translateX(-50px) rotate(-6deg)}100%{transform:translateX(50px) rotate(-6deg)}}
@keyframes nv-sway{0%,100%{transform:rotate(-1.2deg) translateY(0)}50%{transform:rotate(1.2deg) translateY(-6px)}}
@keyframes nv-carriage{0%,100%{left:4%}50%{left:84%}}
@keyframes nv-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes nv-spin{to{transform:rotate(360deg)}}
.nv-drift{animation:nv-drift 18s linear infinite alternate}
.nv-sway{animation:nv-sway 7s ease-in-out infinite;transform-origin:50% 0}
.nv-carriage{animation:nv-carriage 3.2s ease-in-out 2.6s infinite}
.nv-marquee{animation:nv-marquee 28s linear infinite}
.nv-story:hover .nv-ring{animation:nv-spin 1.2s linear infinite}
@media (prefers-reduced-motion: reduce){.nv-drift,.nv-sway,.nv-carriage,.nv-marquee{animation:none}}
`;

function Line({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span className="inline-block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

export default function StudioAbout() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % STEPS.length), 2000);
    return () => clearInterval(t);
  }, [reduce]);

  const { isAdmin, src, reload } = useAdminImages();
  const pressImg = src("about1", IMG.printMachine || IMG.hero);
  const fabricImg = src("about2", IMG.printedRack || IMG.hero);

  return (
    <section className="relative overflow-hidden bg-[#FAF9F6]" data-testid="studio-about">
      <style>{CSS}</style>
      <div aria-hidden="true" className="nv-bgword pointer-events-none absolute inset-x-0 top-28 z-0 flex justify-center"><div className="nv-drift select-none whitespace-nowrap leading-none text-[#efe6da]" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "clamp(140px,18vw,300px)" }}>Nayara</div></div>

      <div className="relative z-10 mx-auto grid max-w-[1400px] items-start gap-14 px-6 py-16 lg:grid-cols-[1.15fr_1fr] lg:gap-20 lg:px-12 lg:py-20">
        {/* Text */}
        <div>
          <motion.span initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.32em] text-[#8a5a32]">
            <span className="h-px w-12 bg-[#8a5a32]" />About Nayara Studio
          </motion.span>

          <h2 className="mt-7 font-display text-[clamp(2.6rem,5.2vw,4.8rem)] font-light leading-[1.08] tracking-tight text-[#1A1A1A]">
            <Line delay={0.1}>A digital textile</Line>
            <Line delay={0.25}>manufacturer for</Line>
            <Line delay={0.4}>
              fashion, events &amp;{" "}
              <motion.em className="inline-block font-display italic text-[#8a5a32]"
                initial={{ clipPath: "inset(0 100% 0 0)" }} whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                viewport={{ once: true }} transition={{ duration: 1.6, delay: 1.1, ease: "easeOut" }}>
                décor.
              </motion.em>
            </Line>
          </h2>

          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-8 max-w-xl text-[1.05rem] leading-[1.9] text-[#595959]">
            Nayara Studio is a premium digital textile printing and fabric manufacturing house. We produce fashion fabrics, kurtis, dress materials and event drapes alongside photography, wedding and stage backdrops — printed, fused, cut and stitched entirely in-house.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-4 max-w-xl text-[1.05rem] leading-[1.9] text-[#595959]">
            From boutiques and fashion labels to event companies and photographers, brands trust us for true-to-artwork colour, couture finishing and dependable delivery at any scale.
          </motion.p>

          {/* Process steps joined by a thread */}
          <div className="relative mt-12 max-w-[640px]">
            <svg viewBox="0 0 640 40" preserveAspectRatio="none" className="pointer-events-none absolute -top-6 left-0 h-10 w-full overflow-visible" aria-hidden="true">
              <motion.path d="M10 30 C 90 0, 150 40, 220 20 S 360 0, 430 22 S 570 40, 630 14" fill="none" stroke="#a0714a"
                strokeWidth="1.5" strokeDasharray="6 6" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
                viewport={{ once: true }} transition={{ duration: 2.2, delay: 1.2, ease: "easeInOut" }} />
            </svg>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {STEPS.map((s, i) => {
                const on = i === active;
                return (
                  <motion.div key={s} whileHover={{ y: -6, rotate: -0.6 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    className={`border px-4 pb-5 pt-4 transition-colors duration-500 ${on ? "border-[#2b1d16] bg-[#2b1d16] text-[#FAF9F6]" : "border-[#d9cbb8] text-[#2b1d16]"}`}>
                    <div className="text-[0.68rem] tracking-[0.25em]">0{i + 1}</div>
                    <div className="mt-2 font-display text-2xl italic">{s}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <Link to="/about" className="nv-story mt-11 inline-flex items-center gap-4 text-sm tracking-[0.3em] text-[#8a5a32]" data-testid="about-link">
            <span className="border-b border-[#8a5a32] pb-1.5">OUR STORY</span>
            <span className="nv-ring inline-flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-[#a0714a]">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Link>
        </div>

        {/* Images */}
        <div className="relative min-h-[620px] lg:min-h-[760px]">
          <div className="absolute left-6 top-10 h-[480px] w-[72%] border border-[#d2b48f] lg:h-[560px]" />
          <motion.div className="absolute left-0 top-10 h-[480px] w-[72%] overflow-hidden lg:h-[560px]"
            initial={{ clipPath: "inset(0 0 100% 0)" }} whileInView={{ clipPath: "inset(0 0 0% 0)" }}
            viewport={{ once: true }} transition={{ duration: 2.2, ease: [0.6, 0.05, 0.3, 1] }}>
            <img src={pressImg} alt="Digital textile press" className="h-full w-full object-cover" />
            {isAdmin && <ChangeBtn slot="about1" onDone={reload} />}
            <motion.div aria-hidden="true" className="absolute left-0 right-0 h-3.5 bg-gradient-to-b from-[#2b1d16] to-[#5a3a28] shadow-[0_6px_18px_rgba(0,0,0,.35)]"
              initial={{ top: "-4%" }} whileInView={{ top: "100%" }} viewport={{ once: true }} transition={{ duration: 2.2, ease: [0.6, 0.05, 0.3, 1] }} />
            <span className="absolute bottom-6 left-5 bg-[#FAF9F6] px-3.5 py-2.5 text-[0.68rem] tracking-[0.3em] text-[#2b1d16]">THE PRESS</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.9, ease: EASE }}
            className="absolute right-0 top-[280px] w-[52%] lg:top-[300px]">
            <div className="nv-sway relative h-[400px] overflow-hidden rounded-t-full shadow-[0_30px_60px_rgba(43,29,22,.25)] lg:h-[470px]">
              <motion.img src={fabricImg} alt="Printed fabrics" className="h-full w-full object-cover"
                initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 2.4, ease: EASE }} />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
              <span className="absolute bottom-4 right-6 font-display text-3xl italic text-[#FAF9F6]">Our prints</span>
            </div>
            {isAdmin && <ChangeBtn slot="about2" onDone={reload} />}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
